import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import Layout from "../components/Layout";
import AssetNav from "../components/AssetNav";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";

const LOCS = [["A_STORE", "Store"], ["A_ROOM", "Room"], ["A_REPAIR", "Under Repair"], ["A_DAMAGED", "Damaged Holding"]];

export default function AssetTransfers() {
  const [transfers, setTransfers] = useState([]);
  const [units, setUnits] = useState([]);
  const [items, setItems] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [f, setF] = useState({ kind: "tagged", unit_id: "", item_id: "", qty: 1, from_location: "A_STORE", to_location: "A_ROOM", from_room_id: "", to_room_id: "", reason: "", condition_after: "" });

  const load = async () => {
    const [t, u, i, r] = await Promise.all([
      api.get("/assets/transfers"), api.get("/assets/units"),
      api.get("/assets/items", { params: { active: true } }), api.get("/rooms"),
    ]);
    setTransfers(t.data); setUnits(u.data.filter(x => x.status !== "disposed")); setItems(i.data); setRooms(r.data);
  };
  useEffect(() => { load(); }, []);

  const roomNo = id => rooms.find(r => r.id === id)?.number || "";

  const submit = async () => {
    try {
      const unit = units.find(u => u.id === f.unit_id);
      const payload = {
        kind: f.kind,
        unit_id: f.kind === "tagged" ? f.unit_id : null,
        item_id: f.kind === "bulk" ? f.item_id : null,
        qty: f.kind === "bulk" ? Number(f.qty) : null,
        from_location: f.kind === "tagged" && unit ? unit.location : f.from_location,
        to_location: f.to_location,
        from_room_id: f.kind === "tagged" && unit ? unit.room_id : (f.from_location === "A_ROOM" ? f.from_room_id : null),
        to_room_id: f.to_location === "A_ROOM" ? f.to_room_id : null,
        reason: f.reason || null, condition_after: f.condition_after || null,
      };
      await api.post("/assets/transfers", payload);
      toast.success("Transfer recorded");
      setF({ ...f, unit_id: "", item_id: "", qty: 1, reason: "", condition_after: "" });
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <Layout>
      <div className="mb-4"><h1 className="section-title">Asset Assignment & Transfer</h1></div>
      <AssetNav />
      <Card className="p-4 mb-4">
        <div className="grid grid-cols-2 md:grid-cols-7 gap-3 items-end">
          <div><Label>Type</Label><Select value={f.kind} onValueChange={v => setF({ ...f, kind: v })}><SelectTrigger className="h-11 mt-1" data-testid="transfer-kind"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="tagged">Tagged Asset</SelectItem><SelectItem value="bulk">Bulk Quantity</SelectItem></SelectContent></Select></div>
          {f.kind === "tagged" ? (
            <div className="col-span-2"><Label>Asset</Label><Select value={f.unit_id} onValueChange={v => setF({ ...f, unit_id: v })}><SelectTrigger className="h-11 mt-1" data-testid="transfer-unit"><SelectValue placeholder="—" /></SelectTrigger><SelectContent>{units.map(u => <SelectItem key={u.id} value={u.id}>{u.asset_id} · {u.item_name} ({u.location === "A_ROOM" ? `Room ${roomNo(u.room_id)}` : u.location.replace("A_", "")})</SelectItem>)}</SelectContent></Select></div>
          ) : (
            <>
              <div><Label>Item</Label><Select value={f.item_id} onValueChange={v => setF({ ...f, item_id: v })}><SelectTrigger className="h-11 mt-1" data-testid="transfer-item"><SelectValue placeholder="—" /></SelectTrigger><SelectContent>{items.filter(i => i.tracking_mode !== "tagged").map(i => <SelectItem key={i.id} value={i.id}>{i.name}</SelectItem>)}</SelectContent></Select></div>
              <div><Label>Qty</Label><Input type="number" min="1" value={f.qty} onChange={e => setF({ ...f, qty: e.target.value })} className="h-11 mt-1" data-testid="transfer-qty" /></div>
            </>
          )}
          {f.kind === "bulk" && (
            <div><Label>From</Label><Select value={f.from_location} onValueChange={v => setF({ ...f, from_location: v })}><SelectTrigger className="h-11 mt-1" data-testid="transfer-from"><SelectValue /></SelectTrigger><SelectContent>{LOCS.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent></Select></div>
          )}
          {f.kind === "bulk" && f.from_location === "A_ROOM" && (
            <div><Label>From Room</Label><Select value={f.from_room_id} onValueChange={v => setF({ ...f, from_room_id: v })}><SelectTrigger className="h-11 mt-1" data-testid="transfer-from-room"><SelectValue placeholder="—" /></SelectTrigger><SelectContent>{rooms.map(r => <SelectItem key={r.id} value={r.id}>Room {r.number}</SelectItem>)}</SelectContent></Select></div>
          )}
          <div><Label>To</Label><Select value={f.to_location} onValueChange={v => setF({ ...f, to_location: v })}><SelectTrigger className="h-11 mt-1" data-testid="transfer-to"><SelectValue /></SelectTrigger><SelectContent>{LOCS.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent></Select></div>
          {f.to_location === "A_ROOM" && (
            <div><Label>To Room</Label><Select value={f.to_room_id} onValueChange={v => setF({ ...f, to_room_id: v })}><SelectTrigger className="h-11 mt-1" data-testid="transfer-to-room"><SelectValue placeholder="—" /></SelectTrigger><SelectContent>{rooms.map(r => <SelectItem key={r.id} value={r.id}>Room {r.number}</SelectItem>)}</SelectContent></Select></div>
          )}
          <div><Label>Reason</Label><Input value={f.reason} onChange={e => setF({ ...f, reason: e.target.value })} className="h-11 mt-1" data-testid="transfer-reason" /></div>
          <div><Button onClick={submit} className="h-11 w-full bg-slate-900 text-white hover:bg-slate-800" data-testid="transfer-submit">Transfer</Button></div>
        </div>
      </Card>

      <Card className="p-0 overflow-x-auto">
        <table className="vr-table" data-testid="transfers-table">
          <thead><tr><th>Number</th><th>Asset / Item</th><th className="num">Qty</th><th>From</th><th>To</th><th>Reason</th><th>By</th><th>At</th></tr></thead>
          <tbody>
            {transfers.map(t => (
              <tr key={t.id}>
                <td className="font-mono text-xs">{t.number}</td>
                <td>{t.asset_id || items.find(i => i.id === t.item_id)?.name || "—"}</td>
                <td className="num">{t.qty}</td>
                <td className="text-xs">{t.from_location?.replace("A_", "")}{t.from_room_id ? ` (${roomNo(t.from_room_id)})` : ""}</td>
                <td className="text-xs">{t.to_location?.replace("A_", "")}{t.to_room_id ? ` (${roomNo(t.to_room_id)})` : ""}</td>
                <td className="text-xs">{t.reason || "—"}</td><td className="text-xs">{t.created_by_name}</td>
                <td className="text-xs whitespace-nowrap">{t.created_at?.slice(0, 16).replace("T", " ")}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </Layout>
  );
}
